import { useState, useEffect, useMemo } from 'react';
import { LogOut } from 'lucide-react';
import useSWR from 'swr';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useAdminInfo } from './DashboarEntreprise/hooksUseretApplication/hookAdmin Info';
import { initiateLogOut } from '../../../vitrine/services/OauthUtils';
import { useTranslation } from '../../../TranslationContext';

const axiosFetcher = async (url: string) => {
  const response = await axios.get(url, {
    withCredentials: true,
  });
  return response.data;
};

export default function Header() {
  const { appId } = useParams<{ appId: string }>();
  const companyId = localStorage.getItem('company');
  const API_BASE_URL = import.meta.env.VITE_API;
  const { adminInfo, isLoading } = useAdminInfo();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [currentTime, setCurrentTime] = useState<string>('');
  const { t } = useTranslation(); // Utiliser le hook de traduction

  // Mettre à jour l'heure actuelle chaque seconde
  useEffect(() => {
    const updateCurrentTime = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const seconds = now.getSeconds().toString().padStart(2, '0');
      setCurrentTime(`${hours}:${minutes}:${seconds}`);
    };

    updateCurrentTime();
    const intervalId = setInterval(updateCurrentTime, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const applicationDataKey = useMemo(() => {
    return companyId && appId ? `${API_BASE_URL}/company/${companyId}/applications/${appId}` : null;
  }, [companyId, appId, API_BASE_URL]);

  // Configuration SWR avec options optimisées
  const swrOptions = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0,
    dedupingInterval: 60000,
    errorRetryCount: 1,
  };

  const { data: companyData } = useSWR(axiosFetcher, swrOptions);
  const { data: applicationData } = useSWR(applicationDataKey, axiosFetcher, swrOptions);

  // Mémoriser les fonctions pour éviter les re-renders
  const getInitials = useMemo(() => {
    return (name?: string, email?: string) => {
      if (name && name.trim() !== '') {
        const parts = name.split(' ');
        return parts
          .map(part => part.charAt(0).toUpperCase())
          .join('')
          .substring(0, 2);
      }
      if (email) {
        return email.split('@')[0].charAt(0).toUpperCase();
      }
      return 'U';
    };
  }, []);

  const displayName = useMemo(() => {
    if (isLoading) return t('loading');
    if (adminInfo?.fullName && adminInfo.fullName.trim() !== '') {
      return adminInfo.fullName;
    }
    return adminInfo?.email || t('user');
  }, [adminInfo, isLoading, t]);

  const companyInitial = useMemo(() => {
    return companyData?.data?.name?.charAt(0) || companyData?.name?.charAt(0) || 'O';
  }, [companyData]);

  const applicationName = useMemo(() => {
   
    return t('applicationName', {
      applicationName: applicationData?.data?.displayName || applicationData?.displayName || 'Application',
    });
  }, [applicationData, t]);

  const companyName = useMemo(() => {
    return t('companyName', {
      companyName: adminInfo?.companyName || companyData?.data?.name || companyData?.name || 'Entreprise',
    });
  }, [adminInfo?.companyName, companyData, t]);

  return (
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4">
            {/* Logo ou icône de la compagnie */}
            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-r from-gray-600 to-gray-800">
              <span className="text-white font-bold text-sm">{companyInitial}</span>
            </div>

            {/* Titres avec style moderne */}
            <div className="flex flex-col space-y-1">
              <span className="text-xs font-medium text-gray-500 tracking-wider">{companyName}</span>
              <h1 className="text-lg font-bold text-gray-900 flex items-center">
                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-gray-800">
                {applicationName}
                </span>
              </h1>
            </div>
          </div>

          {/* Heure actuelle */}
          <div className="flex items-center bg-gray-100 text-gray-800 rounded-lg px-3 py-2">
            <span className="text-sm font-medium">{currentTime}</span>
          </div>

          {/* Informations utilisateur et déconnexion */}
          <div className="flex items-center space-x-4">
            {/* Nom/Email utilisateur */}
            <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
              <span className="text-sm font-medium truncate max-w-xs">{displayName}</span>
            </div>

            {/* Avatar utilisateur */}
            <div className="relative">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-indigo-100 text-indigo-700 font-semibold cursor-pointer">
                {getInitials(adminInfo?.fullName, adminInfo?.email)}
              </div>
            </div>

            {/* Bouton déconnexion */}
            <button
              onClick={() => setShowLogoutModal(true)}
              className="p-2 rounded-lg hover:bg-gray-100 text-red-600"
              aria-label={t('logout')}
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal de confirmation de déconnexion */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm"></div>
          <div className="relative max-w-md w-full mx-4 rounded-lg shadow-lg p-6 bg-white text-gray-900">
            <div className="text-center">
              <div className="mb-4 flex justify-center text-yellow-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-4">{t('confirmLogout')}</h2>
              <p className="mb-6 text-gray-600">{t('logoutQuestion')}</p>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-4 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={initiateLogOut}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700"
                >
                  {t('logout')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
// import { useState, useEffect, useMemo } from 'react';
// import { LogOut } from 'lucide-react';
// import useSWR from 'swr';
// import axios from 'axios';
// import { useParams } from 'react-router-dom';
// import { useAdminInfo } from '../Dashboard/DashboarEntreprise/hooksUseretApplication/hookAdmin Info';
// import { initiateLogOut } from '../../../vitrine/services/OauthUtils';

// const axiosFetcher = async (url: string) => {
//   const response = await axios.get(url, {
//     withCredentials:true,
//     // headers: {
//     //   Authorization: `Bearer ${localStorage.getItem('access_token')}`,
//     // },
//   });
//   return response.data;
// };

// export default function Header() {
//   const { appId } = useParams<{ appId: string }>();
//   const companyId = localStorage.getItem('company');
//   const API_BASE_URL = import.meta.env.VITE_API;
//   const { adminInfo, isLoading } = useAdminInfo();
//   const [showLogoutModal, setShowLogoutModal] = useState(false);
//   const [currentTime, setCurrentTime] = useState<string>('');

//   // Mettre à jour l'heure actuelle chaque seconde
//   useEffect(() => {
//     const updateCurrentTime = () => {
//       const now = new Date();
//       const hours = now.getHours().toString().padStart(2, '0');
//       const minutes = now.getMinutes().toString().padStart(2, '0');
//       const seconds = now.getSeconds().toString().padStart(2, '0');
//       setCurrentTime(`${hours}:${minutes}:${seconds}`);
//     };

//     updateCurrentTime();
//     const intervalId = setInterval(updateCurrentTime, 1000);
//     return () => clearInterval(intervalId);
//   }, []);

//   // // Mémoriser les clés SWR pour éviter les re-renders inutiles
//   // const companyDataKey = useMemo(() => {
//   //   return companyId ? `${API_BASE_URL}/company/${companyId}` : null;
//   // }, [companyId, API_BASE_URL]);

//   const applicationDataKey = useMemo(() => {
//     return companyId && appId ? `${API_BASE_URL}/companies/${companyId}/applications/${appId}` : null;
//   }, [companyId, appId, API_BASE_URL]);

//   // Configuration SWR avec options optimisées
//   const swrOptions = {
//     revalidateOnFocus: false,
//     revalidateOnReconnect: false,
//     refreshInterval: 0,
//     dedupingInterval: 60000, // 1 minute de déduplication
//     errorRetryCount: 1,
//   };

//   // Appels SWR optimisés
//   const { data: companyData } = useSWR(
//     // companyDataKey,
//     axiosFetcher,
//     swrOptions
//   );

//   const { data: applicationData } = useSWR(
//     applicationDataKey,
//     axiosFetcher,
//     swrOptions
//   );

//   // Mémoriser les fonctions pour éviter les re-renders
//   const getInitials = useMemo(() => {
//     return (name?: string, email?: string) => {
//       if (name && name.trim() !== '') {
//         const parts = name.split(' ');
//         return parts
//           .map(part => part.charAt(0).toUpperCase())
//           .join('')
//           .substring(0, 2);
//       }
      
//       if (email) {
//         return email.split('@')[0].charAt(0).toUpperCase();
//       }
      
//       return 'U';
//     };
//   }, []);

//   const displayName = useMemo(() => {
//     if (isLoading) return 'Chargement...';
//     if (adminInfo?.fullName && adminInfo.fullName.trim() !== '') {
//       return adminInfo.fullName;
//     }
//     return adminInfo?.email || 'Utilisateur';
//   }, [adminInfo, isLoading]);

//   const companyInitial = useMemo(() => {
//     return companyData?.data?.name?.charAt(0) || companyData?.name?.charAt(0) || 'O';
//   }, [companyData]);

//   const applicationName = useMemo(() => {
//     return applicationData?.data?.displayName || applicationData?.displayName || 'Application';
//   }, [applicationData]);

//   const companyName = useMemo(() => {
//     return adminInfo?.companyName || companyData?.data?.name || companyData?.name || 'Entreprise';
//   }, [adminInfo?.companyName, companyData]);

//   return (
//     <header className="bg-white shadow-sm">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="flex justify-between items-center h-16">
//           <div className="flex items-center space-x-4">
//             {/* Logo ou icône de la compagnie */}
//             <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-r from-gray-600 to-gray-800">
//               <span className="text-white font-bold text-sm">
//                 {companyInitial}
//               </span>
//             </div>
            
//             {/* Titres avec style moderne */}
//             <div className="flex flex-col space-y-1">
//               <span className="text-xs font-medium text-gray-500 tracking-wider">
//                 {companyName}
//               </span>
//               <h1 className="text-lg font-bold text-gray-900 flex items-center">
//                 {applicationName}
//                 <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-gray-800">
//                   Dashboard
//                 </span>
//               </h1>
//             </div>
//           </div>
          
//           {/* Heure actuelle */}
//           <div className="flex items-center bg-gray-100 text-gray-800 rounded-lg px-3 py-2">
//             <span className="text-sm font-medium">{currentTime}</span>
//           </div>

//           {/* Informations utilisateur et déconnexion */}
//           <div className="flex items-center space-x-4">
//             {/* Nom/Email utilisateur */}
//             <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
//               <span className="text-sm font-medium truncate max-w-xs">
//                 {displayName}
//               </span>
//             </div>

//             {/* Avatar utilisateur */}
//             <div className="relative">
//               <div className="w-10 h-10 rounded-full flex items-center justify-center bg-indigo-100 text-indigo-700 font-semibold cursor-pointer">
//                 {getInitials(adminInfo?.fullName, adminInfo?.email)}
//               </div>
//             </div>

//             {/* Bouton déconnexion */}
//             <button 
//               onClick={() => setShowLogoutModal(true)}
//               className="p-2 rounded-lg hover:bg-gray-100 text-red-600"
//               aria-label="Logout"
//             >
//               <LogOut className="h-5 w-5" />
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Modal de confirmation de déconnexion */}
//       {showLogoutModal && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center">
//           <div className="absolute inset-0 bg-black/30 backdrop-blur-sm"></div>
//           <div className="relative max-w-md w-full mx-4 rounded-lg shadow-lg p-6 bg-white text-gray-900">
//             <div className="text-center">
//               <div className="mb-4 flex justify-center text-yellow-500">
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                 </svg>
//               </div>
//               <h2 className="text-xl font-semibold mb-4">Confirm loging out</h2>
//               <p className="mb-6 text-gray-600">
//                 Do you realy want to log Out?
//               </p>
//               <div className="flex space-x-3 justify-center">
//                 <button 
//                   onClick={() => setShowLogoutModal(false)}
//                   className="px-4 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
//                 >
//                   Cancel
//                 </button>
//                 <button 
//                   onClick={initiateLogOut}
//                   className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700"
//                 >
//                 Log Out
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </header>
//   );
// }
